import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskPipe } from 'ngx-mask';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { AuthService } from '../../../../core/services/auth.service';
import { User, UserRole } from '../../../../core/models/user.model';
import { UserTierInfo } from '../../../../core/models/fee-configuration.model';
import { Modal } from '../../../../shared';
import { UserApiService } from '../../../../core/services/api/user-api.service';
import { FeeConfigurationApiService } from '../../../../core/services/api/fee-configuration-api.service';

@Component({
  selector: 'app-profile',
  imports: [CommonModule, NgxMaskPipe, Modal, TranslocoPipe],
  templateUrl: './profile.html',
})
export class Profile implements OnInit {
  modal = inject(NgbActiveModal);
  readonly authService = inject(AuthService);
  readonly userApiService = inject(UserApiService);
  readonly feeApiService = inject(FeeConfigurationApiService);
  readonly transloco = inject(TranslocoService);

  userId = signal<string | null>(null);
  user = signal<User | null>(null);
  isLoading = signal(true);
  errorMessage = signal('');
  tierInfo = signal<UserTierInfo | null>(null);

  isTomadorCredito = computed(() => this.authService.isTomadorCredito());
  isOwnProfile = computed(() => !!this.userId() && this.userId() === this.authService.currentUser()?.id);

  ngOnInit() {
    const id = this.userId();
    if (!id) {
      this.errorMessage.set(this.transloco.translate('profile.idNotFound'));
      this.isLoading.set(false);
      return;
    }
    this.loadUserData(id);
  }

  private async loadUserData(id: string) {
    try {
      const userData = await this.userApiService.getUserById(id);
      if (userData) {
        this.user.set(userData);
        if (this.isTomadorCredito() && this.isOwnProfile()) {
          void this.feeApiService
            .getMyTier()
            .then((info) => this.tierInfo.set(info))
            .catch(() => undefined);
        }
      } else {
        this.errorMessage.set(this.transloco.translate('profile.loadNotFound'));
      }
    } catch {
      this.errorMessage.set(this.transloco.translate('profile.loadError'));
    } finally {
      this.isLoading.set(false);
    }
  }

  getRoleLabel(role: UserRole): string {
    const keys: Record<UserRole, string> = {
      [UserRole.TomadorCredito]: 'profile.roles.tomadorCredito',
      [UserRole.Conciliador]: 'profile.roles.conciliador',
      [UserRole.Investidores]: 'profile.roles.investidores',
      [UserRole.AuditoriaConsultoria]: 'profile.roles.auditoriaConsultoria',
      [UserRole.Admin]: 'profile.roles.admin',
      [UserRole.Super]: 'profile.roles.super',
    };
    return this.transloco.translate(keys[role]);
  }

  getRoleBadgeClass(role: UserRole): string {
    const classes: Record<UserRole, string> = {
      [UserRole.Admin]: 'bg-secondary',
      [UserRole.TomadorCredito]: 'bg-primary',
      [UserRole.Conciliador]: 'bg-success',
      [UserRole.Investidores]: 'bg-warning',
      [UserRole.AuditoriaConsultoria]: 'bg-info ',
      [UserRole.Super]: 'bg-danger',
    };
    return classes[role];
  }
}
